import { supabase } from './supabase';

const ALLOWED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt']
} as const;

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

// Files are stored under research-files/<userId>/ — storage policies only
// allow users to write inside their own folder.
export async function uploadFile(file: File, userId: string): Promise<{ name: string; path: string; size: number }> {
  if (!file || !userId) {
    throw new Error('File and userId are required');
  }

  if (file.size > MAX_SIZE) {
    throw new Error(`File size exceeds 10MB limit`);
  }

  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  const isValidType = Object.entries(ALLOWED_TYPES).some(
    ([mimeType, extensions]) => file.type === mimeType || extensions.includes(fileExtension)
  );

  if (!isValidType) {
    throw new Error('Invalid file type. Only PDF, DOCX, and TXT files are allowed');
  }

  // Ensure we have a valid MIME type
  let fileType = file.type;
  if (!fileType) {
    // Map file extensions to MIME types if the type is not set
    const mimeTypes = {
      '.pdf': 'application/pdf',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.txt': 'text/plain'
    };
    fileType = mimeTypes[fileExtension] || 'application/octet-stream';
  }

  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = `research-files/${userId}/${timestamp}-${random}-${safeFileName}`;

  const { data, error } = await supabase.storage
    .from('book-files')
    .upload(filePath, file, {
      cacheControl: '3600',
      contentType: fileType,
      upsert: false
    });

  if (error) throw error;
  if (!data?.path) throw new Error('Upload failed');

  return {
    name: file.name,
    path: data.path,
    size: file.size
  };
}